/**
 * CacheStatusIndicator - Shows cache hit/miss status during document loading
 * 
 * Displays a small floating indicator showing whether pages were loaded from
 * cache (fast) or required typesetting (slower).
 */

import React from 'react';

export interface CacheStatusProps {
  /** Whether persistent cache is enabled */
  enabled: boolean;
  /** Whether we're currently loading/checking cache */
  loading: boolean;
  /** Source of loaded pages: 'cache' (fast), 'typeset' (slow), or null (not yet determined) */
  source: 'cache' | 'typeset' | null;
  /** Number of cache hits */
  hitCount: number;
  /** Number of cache misses */
  missCount: number;
  /** Whether to show detailed stats */
  showDetails?: boolean;
  /** Position: 'top-right' | 'bottom-right' | 'top-left' | 'bottom-left' */
  position?: 'top-right' | 'bottom-right' | 'top-left' | 'bottom-left';
}

export const CacheStatusIndicator: React.FC<CacheStatusProps> = ({
  enabled,
  loading,
  source,
  hitCount,
  missCount,
  showDetails = false,
  position = 'bottom-right'
}) => {
  // Don't render if cache is not enabled or nothing to show
  if (!enabled || (!loading && !source)) {
    return null;
  }

  const positionStyles: Record<string, React.CSSProperties> = {
    'top-right': { top: 8, right: 8 },
    'bottom-right': { bottom: 8, right: 8 },
    'top-left': { top: 8, left: 8 },
    'bottom-left': { bottom: 8, left: 8 }
  };

  const getStatusColor = () => {
    if (loading) return '#6b7280'; // gray
    if (source === 'cache') return '#10b981'; // green
    return '#f59e0b'; // amber
  };

  const getStatusIcon = () => {
    if (loading) return '⏳';
    if (source === 'cache') return '⚡';
    return '🔄';
  };

  const getStatusText = () => {
    if (loading) return 'Checking cache...';
    if (source === 'cache') return 'Loaded from cache';
    return 'Typeset from source';
  };

  const totalRequests = hitCount + missCount;
  const hitRate = totalRequests > 0 ? Math.round((hitCount / totalRequests) * 100) : 0;

  return (
    <div
      style={{
        position: 'absolute',
        ...positionStyles[position],
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 4,
        pointerEvents: 'none'
      }}
    >
      {/* Main status badge */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '4px 10px',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 6,
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          fontSize: 12,
          fontFamily: 'system-ui, -apple-system, sans-serif',
          color: getStatusColor(),
          border: `1px solid ${getStatusColor()}20`,
          transition: 'all 0.2s ease'
        }}
      >
        <span style={{ fontSize: 14 }}>{getStatusIcon()}</span>
        <span style={{ fontWeight: 500 }}>{getStatusText()}</span>
      </div>

      {/* Details panel (optional) */}
      {showDetails && totalRequests > 0 && (
        <div
          style={{
            padding: '6px 10px',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            borderRadius: 6,
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            fontSize: 11,
            fontFamily: 'system-ui, -apple-system, sans-serif',
            color: '#4b5563'
          }}
        >
          <div style={{ display: 'flex', gap: 12 }}>
            <span>
              <span style={{ color: '#10b981' }}>●</span> Hits: {hitCount}
            </span>
            <span>
              <span style={{ color: '#f59e0b' }}>●</span> Misses: {missCount}
            </span>
            <span style={{ fontWeight: 500 }}>
              {hitRate}% hit rate
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Compact version for toolbar integration
 */
export const CacheStatusBadge: React.FC<{
  source: 'cache' | 'typeset' | null;
  hitRate?: number;
}> = ({ source, hitRate }) => {
  if (!source) return null;

  const isCache = source === 'cache';

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 6px',
        backgroundColor: isCache ? '#d1fae5' : '#fef3c7',
        color: isCache ? '#065f46' : '#92400e',
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 500
      }}
      title={isCache ? 'Loaded from cache (fast)' : 'Typeset from source'}
    >
      {isCache ? '⚡' : '🔄'}
      {isCache ? 'Cached' : 'Fresh'}
      {hitRate !== undefined && (
        <span style={{ opacity: 0.7 }}>({hitRate}%)</span>
      )}
    </span>
  );
};

export default CacheStatusIndicator;
